import React, { useState } from 'react';
import AdminLayout from './AdminLayout';
import { productsAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Upload, Download, FileSpreadsheet, Check, AlertCircle } from 'lucide-react';

const AdminImportExport = () => {
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      alert('Selecione um arquivo Excel (.xlsx ou .xls)');
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const result = await productsAPI.importExcel(file);
      setImportResult(result);
    } catch (error) {
      alert('Erro na importação: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await productsAPI.exportExcel();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `produtos_terrahydro_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Erro na exportação: ' + (error.response?.data?.detail || error.message));
    } finally { setExporting(false); }
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const blob = await productsAPI.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_produtos_terrahydro.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Erro ao baixar template: ' + (error.response?.data?.detail || error.message));
    } finally { setDownloadingTemplate(false); }
  };

  return (
    <AdminLayout>
      <div>
        <div className="mb-6">
          <h1 data-testid="import-export-title" className="text-2xl font-bold text-gray-800">Importar / Exportar</h1>
          <p className="text-gray-600">Gerencie produtos em massa via planilha Excel</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Import */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload size={20} className="text-[#055509]" />
                Importar Produtos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Faça upload de um arquivo Excel (.xlsx) para importar produtos em massa.
                Use o template para garantir o formato correto.
              </p>
              <label data-testid="import-file-input" className="block">
                <input type="file" accept=".xlsx,.xls" onChange={handleImport} className="hidden" />
                <Button asChild className="w-full bg-[#055509] hover:bg-[#0a7a0f] text-white" disabled={importing}>
                  <span>
                    <Upload size={16} className="mr-2" />
                    {importing ? 'Importando...' : 'Selecionar Arquivo'}
                  </span>
                </Button>
              </label>

              {importResult && (
                <div className={`p-4 rounded-lg ${importResult.errors?.length > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {importResult.errors?.length > 0 ? (
                      <AlertCircle size={16} className="text-yellow-600" />
                    ) : (
                      <Check size={16} className="text-green-600" />
                    )}
                    <span className="font-semibold text-sm">
                      {importResult.imported || 0} produtos importados
                      {importResult.updated ? `, ${importResult.updated} atualizados` : ''}
                    </span>
                  </div>
                  {importResult.errors?.length > 0 && (
                    <ul className="text-xs text-yellow-800 space-y-1">
                      {importResult.errors.map((err, i) => <li key={i}>- {err}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Export */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download size={20} className="text-[#055509]" />
                Exportar Produtos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Baixe todos os produtos em formato Excel para edição ou backup.
              </p>
              <Button data-testid="export-btn" onClick={handleExport} className="w-full bg-[#055509] hover:bg-[#0a7a0f] text-white" disabled={exporting}>
                <Download size={16} className="mr-2" />
                {exporting ? 'Exportando...' : 'Exportar Produtos'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Template */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet size={20} className="text-[#FFB025]" />
              Template de Importação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Baixe o template para garantir que seus dados estejam no formato correto para importação.
            </p>
            <Button data-testid="download-template-btn" onClick={handleDownloadTemplate} variant="outline" disabled={downloadingTemplate}>
              <Download size={16} className="mr-2" />
              {downloadingTemplate ? 'Baixando...' : 'Baixar Template'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminImportExport;
